<template>
  <DialogBase
    :visible.sync="visible"
    title="Chi tiết nhận xét định kỳ"
    size="lg"
    :show-confirm="false"
    cancel-text="Đóng"
    :use-panel="false"
    @cancel="handleClose"
  >
    <div v-if="studentData" class="pev-detail">
      <!-- Header: Student info -->
      <div class="sk-panel">
        <div class="sk-panel-head">
          <div class="sk-panel-icon">
            <i class="el-icon-user" />
          </div>
          <div>
            <p class="sk-panel-title">
              {{ studentData.studentName }}
              <el-tag v-if="!isStudying" size="mini" type="info" class="ml-2">Đã chuyển</el-tag>
            </p>
            <p class="sk-panel-subtitle">Năm {{ year }} — {{ evaluations.length }} lần nhận xét</p>
          </div>
        </div>
      </div>

      <!-- Evaluations list container with scrollable content -->
      <div class="pev-detail-content-wrap sk-u-mt-12">
        <div v-if="evaluations.length === 0" class="pev-detail-empty">
          <i class="el-icon-document" />
          <span>Chưa có dữ liệu nhận xét</span>
        </div>

        <div
          v-for="item in evaluations"
          :key="item.uid"
          class="pev-detail-card"
        >
          <!-- Card header -->
          <div class="pev-detail-card__header">
            <div class="flex items-center gap-2">
              <el-tag size="mini" type="info">Lần {{ item.periodNumber }}</el-tag>
              <span class="pev-detail-date">{{ formatDate(item.date) }}</span>
              <el-tag
                size="mini"
                :type="item.approved ? 'success' : 'warning'"
              >
                {{ item.approved ? 'Đã duyệt' : 'Chờ duyệt' }}
              </el-tag>
            </div>
            <div class="pev-detail-actions">
              <el-button
                v-if="!item.approved"
                type="success"
                icon="el-icon-check"
                size="mini"
                plain
                @click="handleApprove(item)"
              >
                Duyệt
              </el-button>
              <el-button
                type="primary"
                icon="el-icon-edit"
                size="mini"
                plain
                @click="handleEdit(item)"
              >
                Sửa
              </el-button>
              <el-button
                type="danger"
                icon="el-icon-delete"
                size="mini"
                plain
                @click="handleDelete(item)"
              >
                Xoá
              </el-button>
            </div>
          </div>

          <!-- Content in distinct card -->
          <div class="pev-content-card">
            <div class="pev-content-card__header">
              <i class="el-icon-edit-outline" />
              <span>Nội dung nhận xét lần {{ item.periodNumber }}</span>
            </div>
            <div class="pev-content-card__body">{{ item.content }}</div>
          </div>

          <!-- Meta -->
          <div class="pev-detail-card__meta">
            <span><i class="el-icon-user" /> {{ item.createdBy }}</span>
            <span v-if="item.createdDate"> · {{ formatDateTime(item.createdDate) }}</span>
            <span v-if="item.approved && item.approvedBy"> · Duyệt bởi: {{ item.approvedBy }}</span>
          </div>

          <!-- Replies -->
          <div v-if="item.replies && item.replies.length > 0" class="pev-replies">
            <p class="pev-replies__title">
              <i class="el-icon-chat-dot-round" /> Phản hồi ({{ item.replies.length }})
            </p>
            <div
              v-for="reply in item.replies"
              :key="reply.uid"
              class="pev-reply-item"
            >
              <div class="pev-reply-item__meta">
                <span class="pev-reply-item__name">{{ reply.replierName }}</span>
                <el-tag size="mini" :type="roleTagType(reply.role)">{{ roleLabel(reply.role) }}</el-tag>
                <span class="pev-reply-item__date">{{ formatDateTime(reply.createdDate) }}</span>
                <!-- Edit/Delete own replies -->
                <span v-if="isOwnReply(reply)" class="pev-reply-item__actions">
                  <el-button type="text" size="mini" icon="el-icon-edit" @click="startEditReply(item, reply)" />
                  <el-button type="text" size="mini" icon="el-icon-delete" class="pev-reply-delete" @click="handleDeleteReply(item, reply)" />
                </span>
              </div>
              <div v-if="editingReplyUid === reply.uid" class="pev-reply-edit">
                <el-input
                  v-model="editReplyContent"
                  size="small"
                  placeholder="Sửa phản hồi..."
                  maxlength="500"
                  show-word-limit
                  @keyup.enter.native="submitEditReply(item, reply)"
                >
                  <template #append>
                    <el-button icon="el-icon-check" :loading="replyLoading === reply.uid" @click="submitEditReply(item, reply)" />
                  </template>
                </el-input>
                <el-button type="text" size="mini" @click="cancelEditReply">Huỷ</el-button>
              </div>
              <div v-else class="pev-reply-item__content">{{ reply.content }}</div>
            </div>
          </div>

          <!-- Add reply -->
          <div class="pev-add-reply">
            <el-input
              v-model="replyContents[item.uid]"
              size="small"
              placeholder="Viết phản hồi..."
              maxlength="500"
              @keyup.enter.native="handleAddReply(item)"
            >
              <template #append>
                <el-button
                  icon="el-icon-s-promotion"
                  :loading="replyLoading === item.uid"
                  @click="handleAddReply(item)"
                />
              </template>
            </el-input>
          </div>
        </div>

        <!-- Add new period button (only for STUDYING) -->
        <div v-if="isStudying" class="pev-detail-add">
          <el-button
            type="primary"
            plain
            icon="el-icon-plus"
            @click="$emit('add-period', studentData)"
          >
            Tạo nhận xét lần mới
          </el-button>
        </div>
      </div>
    </div>
  </DialogBase>
</template>

<script>
import DialogBase from "@/components/DialogBase.vue";
import PeriodicEvaluationService from "@/modules/evaluation/services/PeriodicEvaluationService";
import NotifyMessage from "@/commonFuncion/NotifyMessage";

const ROLE_MAP = { parent: "Phụ huynh", teacher: "Giáo viên", plus: "Nhà trường" };
const ROLE_TAG_TYPE = { parent: "warning", teacher: "", plus: "success" };

export default {
  name: "PeriodicEvaluationDetailDialog",
  components: { DialogBase },
  props: {
    year: { type: [Number, String], default: "" },
  },
  data() {
    return {
      visible: false,
      studentData: null,
      replyContents: {},
      replyLoading: null,
      editingReplyUid: null,
      editReplyContent: "",
    };
  },
  computed: {
    currentUserId() {
      return this.$store.state.auth.user.id;
    },
    isStudying() {
      return this.studentData && this.studentData.kidStatus === "STUDYING";
    },
    evaluations() {
      if (!this.studentData || !this.studentData.evaluations) return [];
      // Sort by periodNumber ascending
      return [...this.studentData.evaluations].sort((a, b) => a.periodNumber - b.periodNumber);
    },
  },
  methods: {
    open(row) {
      this.studentData = row;
      this.replyContents = {};
      this.editingReplyUid = null;
      this.visible = true;
      this.markEvaluationsRead(row);
    },

    refresh(updatedRow) {
      if (updatedRow) {
        this.studentData = updatedRow;
      }
    },

    handleClose() {
      this.visible = false;
      this.studentData = null;
    },

    // BE determines the caller role from the current token. Read-state APIs
    // are intentionally best-effort because this dialog is also used by
    // roles that may not be allowed to update every read-state field.
    async markEvaluationsRead(studentData) {
      const evaluations = Array.isArray(studentData?.evaluations)
        ? studentData.evaluations
        : [];
      if (!studentData || evaluations.length === 0) return;

      await Promise.all(
        evaluations.map(evaluation => {
          const payload = {
            studentId: studentData.studentId,
            year: Number(this.year),
            evaluationUid: evaluation.uid,
          };
          const requests = [PeriodicEvaluationService.markRead(payload)];
          if (Array.isArray(evaluation.replies) && evaluation.replies.length > 0) {
            requests.push(PeriodicEvaluationService.markRepliesRead(payload));
          }
          return Promise.all(requests).catch(() => null);
        })
      );
    },

    // ─── Actions ─────────────────────────────────────
    handleEdit(evaluation) {
      this.$emit("edit", { studentData: this.studentData, evaluation });
    },

    handleDelete(evaluation) {
      this.$emit("delete", { studentData: this.studentData, evaluation });
    },

    handleApprove(evaluation) {
      this.$emit("approve", { studentData: this.studentData, evaluation });
    },

    // ─── Reply ───────────────────────────────────────
    async handleAddReply(evaluation) {
      const content = (this.replyContents[evaluation.uid] || "").trim();
      if (!content) return;

      this.replyLoading = evaluation.uid;
      try {
        const payload = {
          studentId: this.studentData.studentId,
          year: Number(this.year),
          evaluationUid: evaluation.uid,
          content: content,
        };
        await PeriodicEvaluationService.createReply(payload);
        this.$set(this.replyContents, evaluation.uid, "");
        this.$emit("updated");
      } catch (err) {
        NotifyMessage.leftCornerNotificationError(
          err?.response?.data?.message || "Gửi phản hồi thất bại!"
        );
      } finally {
        this.replyLoading = null;
      }
    },

    isOwnReply(reply) {
      return reply.replierId === this.currentUserId;
    },

    startEditReply(evaluation, reply) {
      this.editingReplyUid = reply.uid;
      this.editReplyContent = reply.content;
    },

    cancelEditReply() {
      this.editingReplyUid = null;
      this.editReplyContent = "";
    },

    async submitEditReply(evaluation, reply) {
      const content = (this.editReplyContent || "").trim();
      if (!content) return;

      this.replyLoading = reply.uid;
      try {
        await PeriodicEvaluationService.updateReply({
          studentId: this.studentData.studentId,
          year: Number(this.year),
          evaluationUid: evaluation.uid,
          replyUid: reply.uid,
          content: content,
        });
        this.cancelEditReply();
        this.$emit("updated");
      } catch (err) {
        NotifyMessage.leftCornerNotificationError(
          err?.response?.data?.message || "Sửa phản hồi thất bại!"
        );
      } finally {
        this.replyLoading = null;
      }
    },

    async handleDeleteReply(evaluation, reply) {
      try {
        await this.$confirm("Xoá phản hồi này?", "Xác nhận", {
          confirmButtonText: "Xoá",
          cancelButtonText: "Huỷ",
          type: "warning",
        });
      } catch {
        return;
      }

      this.replyLoading = reply.uid;
      try {
        await PeriodicEvaluationService.deleteReply({
          studentId: this.studentData.studentId,
          year: Number(this.year),
          evaluationUid: evaluation.uid,
          replyUid: reply.uid,
        });
        this.$emit("updated");
      } catch (err) {
        NotifyMessage.leftCornerNotificationError(
          err?.response?.data?.message || "Xoá phản hồi thất bại!"
        );
      } finally {
        this.replyLoading = null;
      }
    },

    // ─── Utils ───────────────────────────────────────
    roleLabel(role) {
      return ROLE_MAP[role] || role;
    },
    roleTagType(role) {
      return ROLE_TAG_TYPE[role] || "";
    },
    formatDate(val) {
      if (!val) return "";
      const parts = val.split("-");
      if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
      return val;
    },
    formatDateTime(val) {
      if (!val) return "";
      const dt = val.split("T");
      if (dt.length === 2) {
        const parts = dt[0].split("-");
        return `${dt[1].substring(0, 5)} ${parts[2]}/${parts[1]}/${parts[0]}`;
      }
      return val;
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/style/sk-dialog";

.pev-detail {
  padding: 4px 0;
  height: 70vh;
  max-height: 720px;
  min-height: 520px;
  display: flex;
  flex-direction: column;
}

.pev-detail-content-wrap {
  flex: 1;
  overflow-y: auto;
  padding-right: 4px;
  margin-top: 12px;
}

.pev-detail-empty {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 32px;
  justify-content: center;
  color: #94a3b8;
  font-size: 14px;
  i { font-size: 20px; }
}

.pev-detail-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 10px;
  }

  &__content {
    font-size: 14px;
    color: #1e293b;
    line-height: 1.6;
    white-space: pre-wrap;
    margin-bottom: 8px;
  }

  &__meta {
    font-size: 12px;
    color: #64748b;
    margin-top: 10px;
    display: flex;
    align-items: center;
    gap: 4px;

    i {
      color: #2563eb;
    }
  }
}

.pev-content-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
  margin-bottom: 6px;
  min-height: 84px;

  &__header {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
    background: #f1f5f9;
    border-bottom: 1px solid #e2e8f0;
    font-size: 13px;
    font-weight: 600;
    color: #1e293b;

    i {
      color: #2563eb;
    }
  }

  &__body {
    padding: 12px 14px;
    font-size: 13.5px;
    color: #334155;
    line-height: 1.6;
    white-space: pre-wrap;
    word-break: break-word;
    max-height: 200px;
    overflow-y: auto;
  }
}

.pev-detail-actions {
  display: flex;
  gap: 4px;
}

.pev-detail-date {
  font-size: 12px;
  color: #64748b;
}

.pev-detail-add {
  text-align: center;
  padding: 16px 0 4px;
}

/* Replies */
.pev-replies {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px dashed #e2e8f0;

  &__title {
    font-size: 13px;
    font-weight: 600;
    color: #475569;
    margin: 0 0 10px;
    i { color: #2563eb; margin-right: 4px; }
  }
}

.pev-reply-item {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 8px;

  &__meta {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
  }

  &__name {
    font-size: 13px;
    font-weight: 600;
    color: #1e293b;
  }

  &__date {
    font-size: 11px;
    color: #94a3b8;
    margin-left: auto;
  }

  &__actions {
    display: inline-flex;
    gap: 2px;
    margin-left: 8px;
  }

  &__content {
    font-size: 13px;
    color: #475569;
    line-height: 1.5;
  }
}

.pev-reply-delete {
  color: #ef4444 !important;
}

.pev-reply-edit {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
}

.pev-add-reply {
  margin-top: 10px;
  /deep/ .el-input__inner { border-radius: 8px; }
  /deep/ .el-input-group__append { border-radius: 0 8px 8px 0; }
}

/* Helpers */
.flex { display: flex; }
.items-center { align-items: center; }
.gap-2 { gap: 8px; }
.ml-2 { margin-left: 8px; }
</style>
